/**
 * ============================================================================
 * useAuth Hook - Supabase Edition
 * ============================================================================
 * 
 * This hook provides authentication state and methods using Supabase Auth.
 * 
 * Status: ✅ SUPABASE-READY
 * Replaces: client/src/_core/hooks/useAuth.ts (Manus OAuth)
 * 
 * Usage:
 * const { user, loading, error, signIn, signUp, signOut } = useAuth();
 * 
 * ============================================================================
 */

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { trpc } from '../lib/trpc';
import type { User } from '../types/auth';

interface UseAuthReturn {
  user: User | null;
  loading: boolean;
  error: Error | null;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [hasSession, setHasSession] = useState(false);

  // Check if user has a Supabase session
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setHasSession(!!session);
      if (!session) {
        setLoading(false);
      }
    };
    checkSession();
  }, []);

  // Fetch user profile from backend only if session exists
  const { data: userProfile, isLoading: profileLoading, error: profileError } = trpc.auth.me.useQuery(
    undefined,
    {
      enabled: hasSession, // Only fetch if we have a session
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  // Update user state when profile data changes
  useEffect(() => {
    if (hasSession) {
      if (profileLoading) {
        setLoading(true);
      } else if (profileError) {
        console.error('[useAuth] Failed to load user profile:', profileError);
        setError(profileError as Error);
        setUser(null);
        setLoading(false);
        
        // Clear session if backend returns error
        supabase.auth.signOut();
        setHasSession(false);
      } else if (userProfile) {
        setUser({
          id: userProfile.id,
          email: userProfile.email,
          role: userProfile.role,
          firstName: userProfile.firstName,
          lastName: userProfile.lastName,
          companyId: userProfile.companyId,
          isLocked: userProfile.isLocked,
          language: userProfile.language,
        });
        setLoading(false);
      }
    } else {
      setUser(null);
      setLoading(false);
    }
  }, [hasSession, profileLoading, profileError, userProfile]);

  // Listen to auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[useAuth] Auth state changed:', event);
      
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setHasSession(true);
      } else if (event === 'SIGNED_OUT') {
        setHasSession(false);
        setUser(null);
        setLoading(false);
        // Redirect to home page when signed out
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Sign in with email and password
  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        throw signInError;
      }

      setHasSession(true);
    } catch (err) {
      console.error('[useAuth] Sign in failed:', err);
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign up with email and password
  const signUp = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
      });

      if (signUpError) {
        throw signUpError;
      }

      if (data.session) {
        setHasSession(true);
      }
    } catch (err) {
      console.error('[useAuth] Sign up failed:', err);
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setLoading(true);
      setError(null);

      const { error: signOutError } = await supabase.auth.signOut();

      if (signOutError) {
        throw signOutError;
      }

      setHasSession(false);
      setUser(null);
    } catch (err) {
      console.error('[useAuth] Sign out failed:', err);
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Refresh user profile
  const refreshUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setHasSession(!!session);
  };

  return {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    signIn,
    signUp,
    signOut,
    refreshUser,
  };
}

